package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.essntl.features.proposal.domain.model.ProposalFilterOptions
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.ProposalOrderType
import com.essntl.features.proposal.ui.allproposal.AllProposalState
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.dropdown.EsFilterDropDownItem
import com.essntl.uikit.components.dropdown.EsFilterDropDownMenu
import com.essntl.uikit.components.dropdown.EsGenericFilterDropdown
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.rememberEquals
import com.essntl.uikit.theme.esTypography

@Composable
fun ProposalFilterSection(
    state: AllProposalState,
    onFilterOptionsChange: (ProposalFilterOptions) -> Unit,
) {
    val filterOptions = state.filterOptions

    var isFilterByOwnerId by remember { mutableStateOf(false) }
    var isSortFilterExpanded by remember { mutableStateOf(false) }
    val proposalOrderTypes =
        listOf(
            ProposalOrderType.Alphabetical,
            ProposalOrderType.LastCreated,
            ProposalOrderType.StartDate,
            ProposalOrderType.RecentlyChanged,
        )

    EsGenericFilterDropdown(
        filterTitle = filterOptions.selectedProposalStatus?.toString() ?: "Status",
        filterOptions = ProposalModel.Status.entries.map { it },
        selectedOption = filterOptions.selectedProposalStatus,
        onFilterOptionSelected = {
            onFilterOptionsChange(
                filterOptions.copy(
                    selectedProposalStatus = it,
                ),
            )
        },
    )
    Spacer(modifier = Modifier.width(1.spacing))

    // Owner Filter
    val selectedProfileList = rememberContains(
        state.getAllProfile.data.orEmpty(),
        filterOptions.selectedProfileIds,
    ) { profile -> profile.id }

    Box {
        EsMediumOutlineButton(
            text = when (selectedProfileList.size) {
                0 -> "Owner"
                1 -> selectedProfileList.first().initials
                else -> "${selectedProfileList.first().initials}, +${selectedProfileList.size - 1}"
            },
            onClick = {
                isFilterByOwnerId = !isFilterByOwnerId
            },
            suffixIconRes = if (isFilterByOwnerId) EsIcons.ArrowUp else EsIcons.ArrowDown,
        )

        EsFilterDropDownMenu(
            expanded = isFilterByOwnerId,
            onDismissRequest = { isFilterByOwnerId = false },
            list = state.getAllProfile.data.orEmpty(),
            selectedItemIds = filterOptions.selectedProfileIds,
            onClearAll = {
                onFilterOptionsChange(
                    filterOptions.copy(
                        selectedProfileIds = setOf(),
                    ),
                )
            },
            loading = state.getAllProfile.isLoading,
            content = {
                if (state.getAllProfile.isFailure)
                    Text(
                        text = "Failed to load users",
                        style = MaterialTheme.esTypography.p2Medium14,
                        modifier = Modifier
                            .padding(16.dp),
                    )

                state.getAllProfile.data
                    .orEmpty()
                    .forEach { profile ->
                        val isSelected =
                            rememberEquals(profile, filterOptions.selectedProfileIds) { p -> p.id }

                        key(profile.id) {
                            EsFilterDropDownItem(
                                onClick = {
                                    onFilterOptionsChange(
                                        filterOptions.copy(
                                            selectedProfileIds =
                                                if (profile.id in filterOptions.selectedProfileIds)
                                                    filterOptions.selectedProfileIds - profile.id
                                                else
                                                    filterOptions.selectedProfileIds + profile.id,
                                        ),
                                    )
                                },
                                title = profile.firstName.orEmpty() + " " + profile.lastName.orEmpty(),
                                selected = isSelected,
                                modifier = Modifier
                                    .padding(
                                        vertical = .5f.spacing,
                                        horizontal = 1.5f.spacing,
                                    ),
                            )
                        }
                    }
            },
            modifier = Modifier
                .width(308.dp),
        )
    }

    Spacer(modifier = Modifier.width(1.spacing))

    // Sort
    Box {
        EsMediumOutlineButton(
            text = when (filterOptions.selectedProposalOrderType.size) {
                0 -> "Sort"
                else -> filterOptions.selectedProposalOrderType.joinToString(",") {
                    it.orderName
                }
            },
            onClick = {
                isSortFilterExpanded = !isSortFilterExpanded
            },
            suffixIconRes = EsIcons.Sort,
        )

        EsFilterDropDownMenu(
            expanded = isSortFilterExpanded,
            onDismissRequest = { isSortFilterExpanded = false },
            list = proposalOrderTypes,
            selectedItemIds = filterOptions.selectedProposalOrderType,
            clearing = false,
            content = {
                proposalOrderTypes.forEach { order ->
                    val isSelected = rememberEquals(order, filterOptions.selectedProposalOrderType) { it }

                    key(order) {
                        EsFilterDropDownItem(
                            onClick = {
                                onFilterOptionsChange(
                                    filterOptions.copy(
                                        selectedProposalOrderType =
                                            if (order in filterOptions.selectedProposalOrderType)
                                                filterOptions.selectedProposalOrderType - order
                                            else
                                                setOf(order),
                                    ),
                                )
                            },
                            title = order.orderName,
                            selected = isSelected,
                            modifier = Modifier
                                .padding(
                                    vertical = .5f.spacing,
                                    horizontal = 1.5f.spacing,
                                ),
                        )
                    }
                }
            },
            modifier = Modifier
                .width(308.dp),
        )
    }
}